import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCourses } from '../contexts/CourseContext';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeftIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import Header from '../components/Header';
import { submitApplication } from '../services/applicationService';
import { supabase } from '../lib/supabase';
import { ProgramClass } from '../types';
import { getBatchClassApplicationStats, ClassApplicationStats } from '../services/programClassService';
import { getCourseStatusWithClasses } from '../utils/dateUtils';

// 사용자의 기존 신청 정보 타입
interface UserApplication {
  id: string;
  class_id: string;
  status: 'registered' | 'waiting' | 'cancelled';
  class_name?: string;
}

const ProgramDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  // 자녀 관련 상태 제거 - 항상 본인 신청
  const [submitting, setSubmitting] = useState(false);
  const [programClasses, setProgramClasses] = useState<ProgramClass[]>([]);
  const [classStats, setClassStats] = useState<Record<string, ClassApplicationStats>>({});
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  // 사용자의 기존 신청 정보
  const [userApplication, setUserApplication] = useState<UserApplication | null>(null);
  // 수강신청 완료 모달
  const [showResultModal, setShowResultModal] = useState(false);
  const [applicationResult, setApplicationResult] = useState<{
    type: 'registered' | 'waiting' | 'free';
    programName: string;
    studentName: string;
    paymentAmount?: number;
    paymentDeadline?: string;
    waitingNumber?: number;
  } | null>(null);
  // 글로벌리더 캠프 전용 모달
  const [showCampNoticeModal, setShowCampNoticeModal] = useState(false);
  const [campNoticeAgreed, setCampNoticeAgreed] = useState(false);
  const [showCampCompleteModal, setShowCampCompleteModal] = useState(false);
  const [pendingCampClassId, setPendingCampClassId] = useState<string | null>(null);
  const { getCourse, loading: coursesLoading } = useCourses();
  const { user, isStudent } = useAuth();

  const course = getCourse(id || '');

  // 페이지 진입 시 맨 위로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 자녀 목록 로드 제거 - 항상 본인 신청

  // program_classes 로드
  useEffect(() => {
    if (id) {
      loadProgramClasses();
    }
  }, [id]);

  // 사용자의 기존 신청 정보 로드
  useEffect(() => {
    if (id && user) {
      loadUserApplication();
    }
  }, [id, user]);

  const loadUserApplication = async () => {
    if (!user || !id) return;

    try {
      const { data, error } = await supabase
        .from('applications')
        .select(`
          id,
          class_id,
          status,
          program_classes:class_id (class_name)
        `)
        .eq('program_id', id)
        .eq('student_id', user.id)
        .in('status', ['registered', 'waiting'])
        .maybeSingle();

      if (error) {
        console.error('Error loading user application:', error);
        return;
      }

      if (data) {
        setUserApplication({
          id: data.id,
          class_id: data.class_id,
          status: data.status,
          class_name: (data.program_classes as any)?.class_name
        });
      } else {
        setUserApplication(null);
      }
    } catch (error) {
      console.error('Error loading user application:', error);
    }
  };

  const loadProgramClasses = async () => {
    try {
      console.log('Loading program classes for program:', id);

      const { data, error } = await supabase
        .from('program_classes')
        .select('*')
        .eq('program_id', id)
        .eq('is_published', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error loading program classes:', error);
        return;
      }

      console.log('Program classes:', data);

      if (data && data.length > 0) {
        setProgramClasses(data);

        // 모든 반의 신청 현황을 한번에 로드
        const classIds = data.map(c => c.id);
        const stats = await getBatchClassApplicationStats(classIds);
        setClassStats(stats);
      }
    } catch (error) {
      console.error('Error loading program classes:', error);
    }
  };


  // loadChildren 함수 제거 - 항상 본인 신청

  // 로딩 중일 때
  if (coursesLoading) {
    return (
      <div>
        <Header />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#48c9a6] border-r-transparent"></div>
            <p className="mt-4 text-gray-500">강의 정보를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  // 로딩 완료 후 강의를 찾을 수 없을 때
  if (!course) {
    return (
      <div>
        <Header />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="text-center py-8 sm:py-12">
            <p className="text-sm sm:text-base text-gray-500">강의를 찾을 수 없습니다.</p>
            <button
              onClick={() => navigate('/programs')}
              className="mt-3 sm:mt-4 inline-flex items-center text-xs sm:text-sm text-blue-600 hover:text-blue-800"
            >
              <ArrowLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              프로그램 목록으로 돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ko-KR');
  };

  const formatTime = (time: string) => {
    // Remove seconds from time string (HH:MM:SS -> HH:MM)
    if (time && time.includes(':')) {
      const parts = time.split(':');
      if (parts.length >= 2) {
        return `${parts[0]}:${parts[1]}`;
      }
    }
    return time;
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('ko-KR');
  };

  // 신청기간 및 정원 체크 (KST 기준)
  const now = new Date();
  const nowKST = new Date(now.getTime() + (9 * 60 * 60 * 1000));

  // TIME 타입은 "HH:MM:SS" 형식으로 올 수 있으므로 "HH:MM" 추출
  const buttonStartTime = course.registrationStartTime
    ? course.registrationStartTime.substring(0, 5)
    : '00:00';
  const buttonEndTime = course.registrationEndTime
    ? course.registrationEndTime.substring(0, 5)
    : '23:59';

  const startDateTime = new Date(`${course.registrationStartDate}T${buttonStartTime}:00+09:00`);
  const endDateTime = new Date(`${course.registrationEndDate}T${buttonEndTime}:00+09:00`);

  const isBeforeRegistration = nowKST < startDateTime;
  const isAfterRegistration = nowKST > endDateTime;
  const isDuringRegistration = !isBeforeRegistration && !isAfterRegistration;

  // 반별 버튼 상태 결정
  const getButtonStateForClass = (programClass: ProgramClass) => {
    const stats = classStats[programClass.id];
    const registeredCount = stats?.registered_count || 0;
    const waitingCount = stats?.waiting_count || 0;
    const maxStudents = programClass.max_students;
    const maxWaiting = programClass.waiting_list;

    const isRegistrationFull = registeredCount >= maxStudents;
    const isWaitingFull = waitingCount >= maxWaiting;
    const isFullyBooked = isRegistrationFull && isWaitingFull;

    // 신청 기간 체크
    const now = new Date();
    const startTime = programClass.registration_start_time?.substring(0, 5) || '00:00';
    const endTime = programClass.registration_end_time?.substring(0, 5) || '23:59';
    const startDateTime = new Date(`${programClass.registration_start_date}T${startTime}:00+09:00`);
    const endDateTime = new Date(`${programClass.registration_end_date}T${endTime}:00+09:00`);
    const isBeforeRegistration = now < startDateTime;
    const isAfterRegistration = now > endDateTime;

    // 이미 이 프로그램에 신청한 경우
    if (userApplication) {
      // 해당 반에 신청한 경우
      if (userApplication.class_id === programClass.id) {
        return { disabled: true, label: '신청 완료', isApplied: true };
      }
      // 다른 반에 신청한 경우
      return { disabled: true, label: '신청 불가', isOtherClassApplied: true };
    }

    if (!isStudent) {
      return { disabled: true, label: '로그인 필요' };
    }
    if (isBeforeRegistration) {
      return { disabled: true, label: '신청 대기 중' };
    }
    if (isAfterRegistration) {
      return { disabled: true, label: '신청 마감' };
    }
    if (isFullyBooked) {
      return { disabled: true, label: '신청 마감' };
    }
    if (isRegistrationFull) {
      return { disabled: false, label: '대기 신청' };
    }
    return { disabled: false, label: '수강신청' };
  };

  // 반(class) 정보를 고려한 상태 계산
  const getActualStatus = () => {
    if (!course) return 'upcoming';

    // status_mode가 manual이면 course.status 사용
    if (course.status_mode === 'manual') {
      return course.status;
    }

    // 반 정보를 기반으로 상태 계산
    // programClasses가 있으면 반 정보를 고려, 없으면 강좌 정보만 사용
    const classesForStatus = programClasses.map(cls => ({
      registration_start_date: cls.registration_start_date,
      registration_end_date: cls.registration_end_date,
      registration_start_time: cls.registration_start_time,
      registration_end_time: cls.registration_end_time,
      course_start_date: cls.course_start_date,
      course_end_date: cls.course_end_date,
    }));

    return getCourseStatusWithClasses(course, classesForStatus);
  };

  const actualStatus = getActualStatus();

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'upcoming': return '모집예정';
      case 'recruiting': return '모집중';
      case 'ongoing': return '진행중';
      case 'completed': return '종료';
      default: return '모집예정';
    }
  };

  const handleApplication = (classId: string, programClass: ProgramClass) => {
    console.log('=== handleApplication 호출 ===');
    console.log('classId:', classId);
    console.log('programClass:', programClass);
    console.log('programClass.id:', programClass?.id);

    if (!user || !isStudent) {
      navigate('/login');
      return;
    }

    // 신청 기간 체크
    const now = new Date();
    const startTime = programClass.registration_start_time?.substring(0, 5) || '00:00';
    const endTime = programClass.registration_end_time?.substring(0, 5) || '23:59';
    const startDateTime = new Date(`${programClass.registration_start_date}T${startTime}:00+09:00`);
    const endDateTime = new Date(`${programClass.registration_end_date}T${endTime}:00+09:00`);
    const isBeforeRegistration = now < startDateTime;
    const isAfterRegistration = now > endDateTime;

    if (isBeforeRegistration) {
      alert('아직 신청 기간이 아닙니다.\n\n신청 시작일을 확인해주세요.');
      return;
    }

    if (isAfterRegistration) {
      alert('신청 기간이 종료되었습니다.\n\n다음 접수 기간을 확인해주세요.');
      return;
    }

    const stats = classStats[classId];
    const registeredCount = stats?.registered_count || 0;
    const waitingCount = stats?.waiting_count || 0;
    const isRegistrationFull = registeredCount >= programClass.max_students;
    const isWaitingFull = waitingCount >= programClass.waiting_list;
    const isFullyBooked = isRegistrationFull && isWaitingFull;

    if (isFullyBooked) {
      alert('신청이 마감되었습니다.\n\n정원과 대기 인원이 모두 마감되었습니다.');
      return;
    }

    // 선택된 반 설정
    console.log('=== setSelectedClassId 호출 ===');
    console.log('classId:', classId);
    setSelectedClassId(classId);

    // "글로벌리더 하이브리드 캠프" 강의인 경우 특별 안내 모달 먼저 표시
    const isGlobalLeaderCamp = course?.courseName?.includes('글로벌리더') && course?.courseName?.includes('캠프');
    if (isGlobalLeaderCamp) {
      setPendingCampClassId(classId);
      setCampNoticeAgreed(false);
      setShowCampNoticeModal(true);
      return;
    }

    // 항상 본인 신청 (자녀 선택 로직 제거)
    setShowApplicationModal(true);
  };

  // 글로벌리더 캠프 안내 확인 후 신청 진행
  const handleCampNoticeConfirm = () => {
    if (!campNoticeAgreed) {
      alert('안내 사항을 확인하고 동의해주세요.');
      return;
    }
    // 모달 닫지 않고 바로 신청 처리 (로딩 표시됨)
    if (pendingCampClassId) {
      handleCampApplicationSubmit();
    }
  };

  // 글로벌리더 캠프 신청 처리
  const handleCampApplicationSubmit = async () => {
    if (!user || !id || !pendingCampClassId) return;

    setSubmitting(true);

    try {
      // 로딩 UI가 보이도록 최소 딜레이 (UX 개선)
      const minDelay = new Promise(resolve => setTimeout(resolve, 800));

      const requestData = {
        class_id: pendingCampClassId,
      };

      const [response] = await Promise.all([submitApplication(requestData), minDelay]);

      // 먼저 안내 모달 닫기
      setShowCampNoticeModal(false);

      if (response.success && response.data) {
        const { status, waiting_number, program_name, student_name } = response.data;

        if (status === 'waiting') {
          // 대기 신청인 경우 대기번호 모달 표시
          setApplicationResult({
            type: 'waiting',
            programName: program_name || course?.courseName || '',
            studentName: student_name || '',
            waitingNumber: waiting_number ?? undefined,
          });
          setShowResultModal(true);
        } else {
          // 정원 내 신청인 경우 캠프 완료 모달 표시
          setShowCampCompleteModal(true);
        }
        // 신청 현황 다시 로드
        if (id) {
          loadProgramClasses();
          loadUserApplication();
        }
      } else if (response.error) {
        const { code, message } = response.error;
        switch (code) {
          case 'DUPLICATE_APPLICATION':
            alert('이미 이 프로그램에 신청하셨습니다.');
            break;
          case 'FULLY_BOOKED':
            alert('신청이 마감되었습니다.');
            break;
          default:
            alert(message || '신청 처리 중 오류가 발생했습니다.');
        }
      }
    } catch (error) {
      console.error('Application submission error:', error);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
      setPendingCampClassId(null);
    }
  };

  const handleSubmitApplication = async () => {
    console.log('=== handleSubmitApplication 호출 ===');
    console.log('user:', user);
    console.log('id:', id);
    console.log('selectedClassId:', selectedClassId);

    if (!user || !id || !selectedClassId) {
      console.error('❌ 필수 값 누락:', { user: !!user, id, selectedClassId });
      return;
    }

    // 자녀 선택 검증 제거 - 항상 본인 신청

    setSubmitting(true);

    try {
      // 로딩 UI가 보이도록 최소 딜레이 (UX 개선)
      const minDelay = new Promise(resolve => setTimeout(resolve, 800));

      // 항상 본인 신청 (applicant_type, child_id 제거)
      const requestData = {
        class_id: selectedClassId,
      };

      console.log('=== submitApplication 요청 데이터 ===');
      console.log('requestData:', requestData);

      const [response] = await Promise.all([submitApplication(requestData), minDelay]);

      console.log('=== submitApplication 응답 ===');
      console.log('Submit application response:', response);

      if (response.success && response.data) {
        const {
          status,
          waiting_number,
          program_name,
          student_name,
          payment_required,
          payment_amount,
          payment_deadline
        } = response.data;

        console.log('=== 신청 결과 DEBUG ===');
        console.log('status:', status);
        console.log('payment_required:', payment_required);
        console.log('payment_amount:', payment_amount);
        console.log('payment_deadline:', payment_deadline);
        console.log('전체 response.data:', response.data);
        console.log('=====================');

        // 먼저 첫 번째 모달 닫기
        setShowApplicationModal(false);

        if (status === 'registered') {
          // 결제가 필요한 경우
          if (payment_required && payment_amount && payment_amount > 0) {
            // 익일 12시 계산
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(12, 0, 0, 0);
            const deadlineStr = `${tomorrow.getFullYear()}.${String(tomorrow.getMonth() + 1).padStart(2, '0')}.${String(tomorrow.getDate()).padStart(2, '0')}, 12시`;

            setApplicationResult({
              type: 'registered',
              programName: program_name || '',
              studentName: student_name || '',
              paymentAmount: payment_amount,
              paymentDeadline: deadlineStr,
            });
            setShowResultModal(true);
          } else {
            // 무료 강좌
            setApplicationResult({
              type: 'free',
              programName: program_name || '',
              studentName: student_name || '',
            });
            setShowResultModal(true);
          }
        } else if (status === 'waiting') {
          setApplicationResult({
            type: 'waiting',
            programName: program_name || '',
            studentName: student_name || '',
            waitingNumber: waiting_number ?? undefined,
          });
          setShowResultModal(true);
        }

        // 신청 현황 다시 로드
        if (id) {
          loadProgramClasses();
          loadUserApplication();
        }
      } else if (response.error) {
        // 에러 처리
        const { code, message } = response.error;

        switch (code) {
          case 'UNAUTHORIZED':
            if (window.confirm('로그인이 필요합니다.\n로그인 페이지로 이동하시겠습니까?')) {
              navigate('/login');
            }
            break;
          case 'DUPLICATE_APPLICATION':
            alert('이미 이 프로그램에 신청하셨습니다.\n\n다른 반으로 변경하시려면 먼저 마이페이지에서 기존 신청을 취소한 후 다시 신청해주세요.');
            setShowApplicationModal(false);
            // 신청 정보 다시 로드
            loadUserApplication();
            break;
          case 'FULLY_BOOKED':
            alert('신청이 마감되었습니다.\n\n정원과 대기 인원이 모두 마감되었습니다.\n다음 기수 모집을 기다려주세요.');
            setShowApplicationModal(false);
            break;
          case 'REGISTRATION_NOT_STARTED':
            alert('아직 신청 기간이 아닙니다.\n\n신청 시작일을 확인해주세요.');
            setShowApplicationModal(false);
            break;
          case 'REGISTRATION_ENDED':
            alert('신청 기간이 종료되었습니다.\n\n다음 접수 기간을 확인해주세요.');
            setShowApplicationModal(false);
            break;
          case 'PROGRAM_NOT_FOUND':
            alert('프로그램을 찾을 수 없습니다.');
            setShowApplicationModal(false);
            break;
          default:
            alert(message || '신청 처리 중 오류가 발생했습니다.\n잠시 후 다시 시도해주세요.');
        }
      }
    } catch (error) {
      console.error('Application submission error:', error);
      alert('네트워크 오류가 발생했습니다.\n\n인터넷 연결을 확인하고 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const getApplicationStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return '접수완료';
      case 'approved': return '결제대기';
      case 'rejected': return '신청취소';
      case 'waiting': return '대기중';
      case 'cancelled': return '신청취소';
      default: return status;
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <Header />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-8 sm:pb-12">
        {/* 뒤로가기 버튼 */}
        <button
          onClick={() => navigate('/programs')}
          className="mb-4 sm:mb-6 md:mb-8 inline-flex items-center text-sm sm:text-base font-semibold text-black hover:text-green-600 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" />
          목록
        </button>

        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {/* 헤더 */}
          <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 border-b border-gray-200">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-black">{course.courseName}</h1>
              </div>
            </div>
          </div>

          {/* 메인 컨텐츠 */}
          <div className="p-4 sm:p-6">
            {/* 이미지와 정보 테이블 */}
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-8">
              {/* 왼쪽: 대표 이미지 */}
              <div className="lg:w-1/3 flex-shrink-0">
                {course.courseImage ? (
                  <img
                    src={course.courseImage}
                    alt={course.courseName}
                    className="w-full h-full rounded-lg shadow-md object-cover"
                  />
                ) : (
                  <div className="w-full h-full min-h-[250px] sm:min-h-[300px] bg-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-gray-400 text-sm sm:text-base md:text-lg">이미지 없음</span>
                  </div>
                )}
              </div>

              {/* 오른쪽: 정보 테이블 */}
              <div className="lg:w-2/3 overflow-x-auto flex flex-col">
                <table className="w-full h-full border-collapse border border-gray-300 table-fixed">
                  <colgroup>
                    <col className="w-[30%]" />
                    <col className="w-[70%]" />
                  </colgroup>
                  <tbody>
                  <tr>
                    <th scope="row" className="border border-gray-300 bg-gray-100 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-left text-xs sm:text-sm font-semibold text-black">
                      대상
                    </th>
                    <td className="border border-gray-300 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm text-black">
                      {course.grade || '전체'}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="border border-gray-300 bg-gray-100 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-left text-xs sm:text-sm font-semibold text-black">
                      운영일정
                    </th>
                    <td className="border border-gray-300 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm text-black">
                      {course.schedule || '-'}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="border border-gray-300 bg-gray-100 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-left text-xs sm:text-sm font-semibold text-black">
                      모집방법
                    </th>
                    <td className="border border-gray-300 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm text-black">
                      온라인 {course.selectionMethod === 'first-come' ? '선착순' : '추첨'}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className="border border-gray-300 bg-gray-100 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-left text-xs sm:text-sm font-semibold text-black">
                      수강료
                    </th>
                    <td className="border border-gray-300 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm text-black">
                      {course.tuition || '무료'}
                      {course.materialFee && ` (재료비 ${course.materialFee})`}
                    </td>
                  </tr>
                  {course.location && (
                  <tr>
                    <th scope="row" className="border border-gray-300 bg-gray-100 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-left text-xs sm:text-sm font-semibold text-black">
                      장소
                    </th>
                    <td className="border border-gray-300 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm text-black">
                      {course.location}
                    </td>
                  </tr>
                  )}
                  <tr>
                    <th scope="row" className="border border-gray-300 bg-gray-100 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-left text-xs sm:text-sm font-semibold text-black">
                      문의
                    </th>
                    <td className="border border-gray-300 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm text-black">
                      {course.contactPhone || '031-863-3522'}
                    </td>
                  </tr>
                  {course.remarks && (
                    <tr>
                      <th scope="row" className="border border-gray-300 bg-gray-100 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-left text-xs sm:text-sm font-semibold text-black">
                        비고
                      </th>
                      <td className="border border-gray-300 px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm text-black">
                        {course.remarks}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* 버튼 영역 */}
          <div className="mt-4 sm:mt-6 flex justify-end">
            <button
              onClick={() => {
                window.scrollTo(0, 0);
                navigate('/programs');
              }}
              className="px-4 sm:px-6 py-1.5 sm:py-2 border border-gray-300 rounded text-xs sm:text-sm font-medium text-black bg-white hover:bg-gray-50 transition-colors"
            >
              목록
            </button>
          </div>

            {/* 반별 신청 현황 테이블 - show_in_registration이 true일 때만 표시 */}
            {course.show_in_registration && (
            <div className="mt-8 sm:mt-10 md:mt-12 pt-8 sm:pt-10 md:pt-12 border-t-2 border-gray-300">
              <h2 className="text-2xl sm:text-3xl font-bold text-black mb-6 sm:mb-8">반별 신청 현황</h2>

              {programClasses.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-gray-300">
                  <p className="text-lg text-gray-600">현재 모집 중인 반이 없습니다.</p>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="w-full border-collapse border-2 border-gray-300 min-w-[900px]">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base font-bold text-gray-900 whitespace-nowrap">
                          반 이름
                        </th>
                        <th className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base font-bold text-gray-900 whitespace-nowrap">
                          모집기간
                        </th>
                        <th className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base font-bold text-gray-900 whitespace-nowrap">
                          교육기간
                        </th>
                        <th className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base font-bold text-gray-900 whitespace-nowrap">
                          요일
                        </th>
                        <th className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base font-bold text-gray-900 whitespace-nowrap">
                          시간
                        </th>
                        <th className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base font-bold text-gray-900 whitespace-nowrap">
                          신청현황
                        </th>
                        <th className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base font-bold text-gray-900 whitespace-nowrap">
                          대기현황
                        </th>
                        <th className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base font-bold text-gray-900 whitespace-nowrap">
                          신청
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {programClasses.map((programClass) => {
                        const stats = classStats[programClass.id];
                        const registeredCount = stats?.registered_count || 0;
                        const waitingCount = stats?.waiting_count || 0;
                        const buttonState = getButtonStateForClass(programClass);

                        // 마감 여부 직접 계산 (로그인 상태와 무관)
                        const now = new Date();
                        const endTime = programClass.registration_end_time?.substring(0, 5) || '23:59';
                        const endDateTime = new Date(`${programClass.registration_end_date}T${endTime}:00+09:00`);
                        const isAfterRegistration = now > endDateTime;
                        const isFullyBooked = registeredCount >= programClass.max_students && waitingCount >= programClass.waiting_list;
                        const isClosed = isAfterRegistration || isFullyBooked;

                        return (
                          <tr key={programClass.id} className="hover:bg-gray-50 transition-colors">
                            <td className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base text-gray-900 font-semibold whitespace-nowrap">
                              {programClass.class_name}
                            </td>
                            <td className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base text-gray-900 whitespace-nowrap">
                              {formatDate(programClass.registration_start_date)} ~ {formatDate(programClass.registration_end_date)}
                            </td>
                            <td className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base text-gray-900 whitespace-nowrap">
                              {formatDate(programClass.course_start_date)} ~ {formatDate(programClass.course_end_date)}
                            </td>
                            <td className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base text-gray-900 whitespace-nowrap">
                              {programClass.course_days?.join(', ')}
                            </td>
                            <td className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base text-gray-900 whitespace-nowrap">
                              {formatTime(programClass.course_start_time || '')} ~ {formatTime(programClass.course_end_time || '')}
                            </td>
                            <td className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base whitespace-nowrap">
                              {isClosed ? (
                                <span className="font-medium text-gray-400">마감</span>
                              ) : (
                                <>
                                  <span className={`font-bold text-lg ${
                                    registeredCount >= programClass.max_students
                                      ? 'text-red-600'
                                      : registeredCount >= programClass.max_students * 0.8
                                      ? 'text-yellow-600'
                                      : 'text-green-600'
                                  }`}>
                                    {registeredCount}
                                  </span>
                                  <span className="text-gray-900 font-medium"> / {programClass.max_students}명</span>
                                </>
                              )}
                            </td>
                            <td className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center text-sm sm:text-base whitespace-nowrap">
                              {isClosed ? (
                                <span className="font-medium text-gray-400">마감</span>
                              ) : (
                                <>
                                  <span className={`font-bold text-lg ${
                                    programClass.waiting_list > 0 && waitingCount >= programClass.waiting_list
                                      ? 'text-red-600'
                                      : programClass.waiting_list > 0 && waitingCount >= programClass.waiting_list * 0.8
                                      ? 'text-yellow-600'
                                      : 'text-green-600'
                                  }`}>
                                    {waitingCount}
                                  </span>
                                  <span className="text-gray-900 font-medium"> / {programClass.waiting_list}명</span>
                                </>
                              )}
                            </td>
                            <td className="border-2 border-gray-300 px-3 sm:px-4 py-3 sm:py-4 text-center whitespace-nowrap">
                              <button
                                onClick={() => handleApplication(programClass.id, programClass)}
                                disabled={buttonState.disabled && buttonState.label !== '로그인 필요'}
                                className={`px-6 py-3 rounded-lg text-base font-bold text-white transition-colors shadow-md min-w-[120px] ${
                                  buttonState.disabled
                                    ? buttonState.label === '로그인 필요'
                                      ? 'bg-indigo-500 hover:bg-indigo-600 cursor-pointer'
                                      : 'bg-gray-400 cursor-not-allowed'
                                    : registeredCount >= programClass.max_students
                                    ? 'bg-yellow-600 hover:bg-yellow-700 hover:shadow-lg'
                                    : 'bg-green-600 hover:bg-green-700 hover:shadow-lg'
                                }`}
                              >
                                {buttonState.label}
                              </button>
                              {/* 다른 반에 신청한 경우 안내 문구 */}
                              {(buttonState as any).isOtherClassApplied && userApplication && (
                                <p className="text-xs text-slate-500 mt-1">
                                  {userApplication.class_name} 신청됨
                                </p>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            )}

            {/* 상세 내용 */}
            {course.courseDescription && (
              <div className="mt-8 sm:mt-10 md:mt-12 pt-6 sm:pt-8 md:pt-10 border-t-2 border-gray-300">
                <h2 className="text-xl sm:text-2xl font-bold text-black mb-4 sm:mb-6">프로그램 안내</h2>
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: course.courseDescription }}
                />
              </div>
            )}
          </div>
        </div>

        {/* 하단 목록 버튼 */}
        <div className="mt-8 sm:mt-10 text-center">
          <button
            onClick={() => {
              window.scrollTo(0, 0);
              navigate('/programs');
            }}
            className="px-8 sm:px-10 md:px-12 py-2.5 sm:py-3 md:py-3.5 border-2 border-gray-300 rounded-lg text-sm sm:text-base font-semibold text-black bg-white hover:bg-gray-50 transition-colors shadow-md hover:shadow-lg"
          >
            목록
          </button>
        </div>
      </div>

      {/* 수강신청 완료 모달 */}
      {showResultModal && applicationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 max-w-sm w-full flex flex-col">
            {/* 컨텐츠 */}
            <div className="p-6">
              {/* 유료 강좌 신청 완료 */}
              {applicationResult.type === 'registered' && applicationResult.paymentAmount && (
                <>
                  <div className="text-center mb-8 pt-2">
                    <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5">
                      <svg className="w-7 h-7 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-lg font-semibold text-slate-800 mb-1">신청이 완료되었습니다</p>
                    <p className="text-sm text-slate-500">{applicationResult.programName}</p>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between items-center py-3 border-b border-slate-100">
                      <span className="text-sm text-slate-600">결제 금액</span>
                      <span className="text-base font-semibold text-slate-800">{applicationResult.paymentAmount.toLocaleString()}원</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-slate-100">
                      <span className="text-sm text-slate-600">결제 기한</span>
                      <span className="text-sm font-medium text-slate-800">{applicationResult.paymentDeadline}</span>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-700 leading-relaxed mb-3">
                      <span className="font-semibold">'나중에 결제'</span> 선택 시 <span className="font-semibold">마이페이지</span>에서 결제 가능합니다.<br />
                      <span className="font-semibold">무통장입금</span>은 기한 내 입금해 주세요.
                    </p>
                    <div className="border-t border-slate-200 pt-3">
                      <p className="text-xs text-slate-600 mb-1">입금계좌</p>
                      <p className="text-sm font-semibold text-slate-800">농협 301-0375-1419-51</p>
                      <p className="text-xs text-slate-600">㈜한원글로벌 동두천지점</p>
                    </div>
                  </div>
                </>
              )}

              {/* 무료 강좌 신청 완료 */}
              {applicationResult.type === 'free' && (
                <div className="text-center py-8">
                  <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5">
                    <svg className="w-7 h-7 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-lg font-semibold text-slate-800 mb-1">신청이 완료되었습니다</p>
                  <p className="text-sm text-slate-500 mb-6">{applicationResult.programName}</p>

                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                    무료 강좌
                  </span>
                  <p className="text-xs text-slate-500 mt-3">별도 결제가 필요하지 않습니다</p>
                </div>
              )}

              {/* 대기 신청 완료 */}
              {applicationResult.type === 'waiting' && (
                <>
                  <div className="text-center mb-6 pt-2">
                    <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-5">
                      <svg className="w-7 h-7 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-lg font-semibold text-slate-800 mb-1">대기 신청이 완료되었습니다</p>
                    <p className="text-sm text-slate-500">{applicationResult.programName}</p>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-6 text-center">
                    <p className="text-xs text-slate-500 mb-2">대기 번호</p>
                    <p className="text-4xl font-semibold text-blue-500 mb-4">{applicationResult.waitingNumber}<span className="text-lg font-normal text-slate-500 ml-1">번</span></p>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      정원에 여석이 생기면<br />등록하신 연락처로 안내드립니다
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* 하단 버튼 영역 */}
            <div className="p-4 border-t border-slate-100">
              {applicationResult.type === 'registered' && applicationResult.paymentAmount && (
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowResultModal(false);
                      setApplicationResult(null);
                    }}
                    className="flex-1 px-4 py-3 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 bg-white hover:bg-slate-50 transition-colors"
                  >
                    나중에 결제
                  </button>
                  <button
                    onClick={() => {
                      setShowResultModal(false);
                      setApplicationResult(null);
                      navigate('/mypage?tab=applications');
                    }}
                    className="flex-1 px-4 py-3 bg-emerald-500 text-white text-sm font-medium rounded-xl hover:bg-emerald-600 transition-colors"
                  >
                    결제하기
                  </button>
                </div>
              )}
              {applicationResult.type === 'free' && (
                <button
                  onClick={() => {
                    setShowResultModal(false);
                    setApplicationResult(null);
                  }}
                  className="w-full px-4 py-3 bg-emerald-500 text-white text-sm font-medium rounded-xl hover:bg-emerald-600 transition-colors"
                >
                  확인
                </button>
              )}
              {applicationResult.type === 'waiting' && (
                <button
                  onClick={() => {
                    setShowResultModal(false);
                    setApplicationResult(null);
                  }}
                  className="w-full px-4 py-3 bg-blue-500 text-white text-sm font-medium rounded-xl hover:bg-blue-600 transition-colors"
                >
                  확인
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showApplicationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 sm:p-8 max-w-md w-full">
            {/* 로딩 중일 때 */}
            {submitting ? (
              <div className="py-8 text-center">
                <div className="w-16 h-16 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-6"></div>
                <p className="text-lg font-semibold text-gray-800 mb-2">신청 처리 중...</p>
                <p className="text-sm text-gray-500">잠시만 기다려주세요</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h3 className="text-xl sm:text-2xl font-bold text-black">수강 신청</h3>
                  <button
                    onClick={() => setShowApplicationModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4 sm:space-y-5">
                  {/* 프로그램 정보 */}
                  <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">프로그램</p>
                    <p className="text-sm sm:text-base font-semibold text-black">{course.courseName}</p>
                  </div>

                  {/* 본인 신청 안내 (자녀 선택 UI 제거) */}
                  <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
                    <p className="text-xs sm:text-sm text-blue-800">
                      본인 명의로 신청됩니다.
                    </p>
                  </div>

                  {/* 버튼 영역 */}
                  <div className="flex gap-2 sm:gap-3 pt-2">
                    <button
                      onClick={() => setShowApplicationModal(false)}
                      className="flex-1 px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg text-sm sm:text-base font-semibold text-black bg-white hover:bg-gray-50 transition-colors"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleSubmitApplication}
                      className="flex-1 px-4 py-2.5 sm:py-3 bg-green-600 text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-green-700 transition-colors shadow-lg"
                    >
                      신청하기
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 글로벌리더 캠프 - 1단계: 홈스테이 안내 확인 모달 */}
      {showCampNoticeModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full overflow-hidden">
            {/* 로딩 중일 때 */}
            {submitting ? (
              <div className="px-6 py-12 text-center">
                <div className="w-16 h-16 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-6"></div>
                <p className="text-lg font-semibold text-gray-800 mb-2">신청 처리 중...</p>
                <p className="text-sm text-gray-500">잠시만 기다려주세요</p>
              </div>
            ) : (
              <>
                <div className="px-6 py-5">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">온라인 홈스테이 안내</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    미국 온라인 홈스테이 시간은 <span className="font-medium text-gray-900">토요일 오전 9시~11시</span> 중 배정 후 개별 안내드립니다.
                  </p>
                  <p className="text-sm text-gray-900 font-medium mt-4">
                    1/24(토), 1/31(토), 2/7(토), 2/14(토)
                  </p>
                  <label className="flex items-center gap-2 mt-5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={campNoticeAgreed}
                      onChange={(e) => setCampNoticeAgreed(e.target.checked)}
                      className="w-4 h-4 rounded-sm border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-gray-700">위 내용을 확인했습니다</span>
                  </label>
                </div>
                <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowCampNoticeModal(false);
                      setPendingCampClassId(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleCampNoticeConfirm}
                    disabled={!campNoticeAgreed}
                    className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    신청하기
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 글로벌리더 캠프 - 2단계: 신청 완료 및 입금 안내 모달 */}
      {showCampCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-lg max-w-md w-full flex flex-col max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-lg font-bold text-gray-900">수강 신청이 완료되었습니다.</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-4 text-sm text-gray-700">
                <p>
                  수강료는 <span className="font-semibold">신청 완료 다음 날 12시 이전</span>까지 아래 계좌로 입금해주시기 바랍니다.
                </p>
                <p className="text-red-600 font-medium">
                  기한 내 입금 확인이 되지 않을 경우 자동 취소되오니 정해진 시간 내 입금 부탁드립니다.
                </p>

                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-gray-900 mb-2">◆ 입금 안내</p>
                  <div className="space-y-1 ml-2">
                    <p><span className="text-gray-500">수강료:</span> <span className="font-semibold">6만 원</span></p>
                    <p><span className="text-gray-500">입금계좌:</span> <span className="font-semibold">농협 301-0375-1419-51</span></p>
                    <p className="text-gray-600">㈜한원글로벌 동두천지점</p>
                    <p className="mt-2 text-blue-700">
                      * 입금자명은 수강자 이름(<span className="font-semibold">{user?.name || '홍길동'}</span>)과 회원가입한 전화번호 뒤 4자리(<span className="font-semibold">{(user?.user_type === 'minor' && user?.guardian_phone ? user.guardian_phone : user?.phone)?.slice(-4) || '0000'}</span>)로 넣어주세요. 예) <span className="font-semibold">{user?.name || '홍길동'}{(user?.user_type === 'minor' && user?.guardian_phone ? user.guardian_phone : user?.phone)?.slice(-4) || '0000'}</span>
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-gray-900 mb-2">◆ 감면 혜택 안내</p>
                  <div className="space-y-1 ml-2 text-gray-600">
                    <p>* 감면대상자는 신청 완료 후 7일 이내 증빙서류 제출 및 확인을 통해 감면금액만큼 환불해드립니다.</p>
                    <p>* 감면증빙서류 제출방법<br />
                      <span className="ml-2">이메일: help.dgtc@gmail.com</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-100">
              <button
                onClick={() => setShowCampCompleteModal(false)}
                className="w-full px-4 py-3 bg-emerald-500 text-white text-sm font-medium rounded-xl hover:bg-emerald-600 transition-colors"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramDetail;